import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MIN_PASSWORD_LENGTH = 8;

function AdminForgotPassword() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          username: username.trim(),
          resetToken: resetToken.trim(),
          newPassword
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Reset failed');
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Reset failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[#151515] border border-white/10 rounded-2xl p-8 shadow-xl text-center">
          <div className="mb-4 text-green-400 text-lg font-medium">Password reset successfully.</div>
          <p className="text-gray-400 text-sm">Redirecting to admin login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-[#151515] border border-white/10 rounded-2xl p-8 shadow-xl">
        <h1 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: 'Lexend, sans-serif' }}>
          Reset admin password
        </h1>
        <p className="text-sm text-gray-400 mb-6">
          Enter the admin username, the reset token (from your server config), and a new password.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Admin username</label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#676DFF]"
              placeholder="e.g. admin"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Reset token</label>
            <input
              type="password"
              autoComplete="off"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#676DFF]"
              placeholder="From ADMIN_RESET_TOKEN (server env)"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#676DFF]"
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0F0F0F] border border-white/10 text-white focus:outline-none focus:border-[#676DFF]"
              required
              minLength={MIN_PASSWORD_LENGTH}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-lg bg-[#676DFF] hover:bg-[#5A60E8] disabled:opacity-60 text-white font-semibold transition-colors"
            style={{ fontFamily: 'Lexend, sans-serif' }}
          >
            {isSubmitting ? 'Resetting...' : 'Reset password'}
          </button>
        </form>

        <Link
          to="/admin/login"
          className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
          style={{ fontFamily: 'Lexend, sans-serif' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to admin login
        </Link>
      </div>
    </div>
  );
}

export default AdminForgotPassword;
